import * as React from 'react';
import { ISearchViewProps } from '../dlvry-search';
import { ISearchFormViewProps } from './dlvry-search.form';

type BlackoutOverlayResult = [ boolean, React.CSSProperties, React.Dispatch<React.SetStateAction<boolean>> ];

export const useBlackoutOverlay = (
    searchProps: ISearchViewProps,
    searchDivRef: React.MutableRefObject<HTMLDivElement | null>,
    isMobilePort: boolean,
    isSearchText: boolean,
    disableOverlay?: boolean
): BlackoutOverlayResult => {
    const [popupPosition, setPopupPosition] = React.useState<React.CSSProperties>({});
    const [isInputFocused, setIsInputFocused] = React.useState<boolean>(false);

    const searchInputRef = ((searchProps.form as ISearchFormViewProps).input as any).ref as React.RefObject<HTMLInputElement>;

    const updatePopupPosition = () => {
        if (!searchDivRef.current) {
            return;
        }

        setPopupPosition({
            top: searchDivRef.current.offsetTop + searchDivRef.current.offsetHeight + 10,
            left: searchDivRef.current.offsetLeft,
            width: searchDivRef.current.offsetWidth
        });
    };

    React.useEffect(() => {
        if (!searchInputRef.current) {
            return;
        }

        const focus = () => { setIsInputFocused(true); };
        const blur = () => { setIsInputFocused(false); };

        searchInputRef.current.addEventListener('focus', focus);
        searchInputRef.current.addEventListener('blur', blur);

        return () => {
            searchInputRef.current!.removeEventListener('focus', focus);
            searchInputRef.current!.removeEventListener('blur', blur);
        };
    }, [searchInputRef.current]);

    React.useEffect(() => {
        if (!searchDivRef.current) {
            return;
        }

        const resize = () => {
            updatePopupPosition();
        };

        window.addEventListener('resize', resize);

        return () => {
            window.removeEventListener('resize', resize);
        };
    }, [searchDivRef, searchDivRef.current, setPopupPosition]);

    const shouldShowOverlay = React.useMemo(() =>
        !disableOverlay && (isInputFocused || isSearchText),
        [disableOverlay, isInputFocused, isSearchText, isMobilePort]
    );

    React.useEffect(() => {
        if (!shouldShowOverlay) {
            updatePopupPosition();
            return;
        }

        document.body.style.position = 'fixed';
        document.body.style.top = '0px';
        document.body.style.right = '17px';
        document.body.style.left = '0px';
        document.body.setAttribute('data-popup', 'true');

        updatePopupPosition();

        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.right = '';
            document.body.style.left = '';
            document.body.removeAttribute('data-popup');
        };
    }, [shouldShowOverlay, searchDivRef.current]);

    if (disableOverlay) {
        return [ false, popupPosition, setIsInputFocused ];
    }

    return [ shouldShowOverlay, popupPosition, setIsInputFocused ];
};
